<template>
  <div class="container maxWidth-sm" v-if="options.length > 0 && !!shortUserDetails">
    <div v-if="!logMessages">
      <div class="row">
        <h1>Delete user "{{ shortUserDetails.firstName }} {{ shortUserDetails.lastName }}"</h1>
      </div>
      <hr />
      <div class="row">
        <div class="col-sm-12">
          <h3>Choose which user data to delete:</h3>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-10 col-sm-offset-1 text-big">
          <!-- Each of the options... -->
          <div class="form-group text-big">
            <a href="javascript:void(0)" @click="selectNone">None</a>
            |
            <a href="javascript:void(0)" @click="selectAll">All</a>
          </div>
          <div
            v-for="option in optionsToDisplay"
            v-bind:key="option.Type"
            class="checkbox text-big"
          >
            <label>
              <input type="checkbox" @change="onChange(option)" v-model="option.selected" />
              {{ option.description }} ({{ option.ExistingRecordCount }})
            </label>
          </div>
          <hr />
          <div class="form-group">
            <button class="btn btn-lg btn-default" @click="cancel">Cancel</button>
            <button
              class="btn btn-lg btn-primary"
              :class="{ invisible: isConfirming }"
              @click="confirm"
              :disabled="!isValid || isConfirming"
              style="margin-left: 10px"
            >
              Delete
            </button>
            <button
              v-if="isConfirming"
              class="btn btn-lg btn-danger"
              @click="deleteUser"
              style="margin-left: 10px"
            >
              Sure?
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="logMessages">
      <div class="row">
        <h3>
          Delete completed for "{{ shortUserDetails.firstName }} {{ shortUserDetails.lastName }}"
        </h3>
      </div>
      <div class="row text-big">
        <p>Here are the actions that were taken:</p>
      </div>
      <div class="row text-big">
        <ul>
          <li v-for="message in logMessages" v-bind:key="message">{{ message }}</li>
        </ul>
      </div>
      <hr />
      <div class="row text-center">
        <div class="col-sm-10 col-sm-offset-1">
          <button class="btn btn-lg btn-primary" @click="done">Done</button>
        </div>
      </div>
    </div>
    <hr />
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { debug as d } from "debug";
import { computed, onMounted, ref } from "vue";
import { ShortUserDetailsApiModel } from "../../api-models";

const debug = d("cs.DeleteUserPage");

export interface UserDeletionDataTypeAndDependencies {
  Type: string;
  DependsOn: string[];
  DependedOnBy: string[];
  ExistingRecordCount: number;
}

export interface UserDeletionOptionsApiModel {
  ShortUserDetails: ShortUserDetailsApiModel;
  Options: UserDeletionDataTypeAndDependencies[];
}

const optionMetadata = {
  UserRecord: { description: "Main user record", visible: true },
  GroupMembership: { description: "Membership of all groups", visible: false },
  DevelopProgress: { description: "Develop progress", visible: true },
  SlackAccessTokens: { description: "Slack access tokens", visible: true },
  MsTeamsConnections: { description: "MS Teams connections", visible: true },
  CustomViewpointsReports: { description: "Custom viewpoints reports", visible: true },
  SubscriptionsAndLicencesOwned: { description: "Owned subscriptions and licences", visible: true },
  UserNotificationInteractions: { description: "Popup notification history", visible: false },
  SoloReviews: { description: "Solo reviews", visible: true },
  ViewpointsOnOthers: { description: "Viewpoints on others", visible: true },
  ViewpointsOnMe: { description: "Viewpoints by others", visible: true },
  EngageQuestionnaires: { description: "Engage questionnaires", visible: true },
  GroupInvitesCreatedByMe: { description: "Group invites created by the user", visible: true },
  GroupInvitesUsedOnlyByMe: { description: "Single-use group invites used", visible: false },
  GroupInviteUses: { description: "All group invites used", visible: false },
  ViewpointsInvitesCreatedByMe: {
    description: "Viewpoints invites created by the user",
    visible: true,
  },
  ViewpointsInviteUsesByMe: { description: "Viewpoints invite uses", visible: false },
  ViewpointsInviteSendsDirectlyToMe: {
    description: "Viewpoints invite sends directly to the user",
    visible: false,
  },
  ActiveLicence: { description: "Active licence", visible: true },
  GiftedLicences: { description: "Licences gifted to other users", visible: true },
  TeamEffectivenessQuestionnaires: {
    description: "Team effectiveness questionnaires",
    visible: true,
  },
  TeamEffectivenessReviews: { description: "Team effectiveness reviews created", visible: false },
  PersonalPathways: { description: "Personal Pathway progress", visible: true },
};

interface DeleteUserPageOption extends UserDeletionDataTypeAndDependencies {
  selected: boolean;
  description: string;
  visible: boolean;
}

const props = defineProps<{ id: string }>();

const shortUserDetails = ref<ShortUserDetailsApiModel | null>(null);
const options = ref<DeleteUserPageOption[]>([]);
const isValid = ref<boolean>(true);
const logMessages = ref<string[] | null>(null);
const isConfirming = ref<boolean>(false);

const optionsToDisplay = computed(() =>
  options.value.filter((o) => o.visible && o.ExistingRecordCount > 0),
);

function selectAll(): void {
  isConfirming.value = false;
  options.value.forEach((o) => {
    o.selected = true;
  });
  isValid.value = true;
}

function selectNone(): void {
  isConfirming.value = false;
  options.value.forEach((o) => {
    o.selected = false;
  });
  isValid.value = false;
}

function cancel(): void {
  window.location.href = `/admin/user/details/${props.id}`;
}

function confirm(): void {
  isConfirming.value = !isConfirming.value;
}

function done(): void {
  const haveDeletedUserEntirely = options.value.some((o) => o.Type === "UserRecord" && o.selected);
  window.location.href = haveDeletedUserEntirely ? "/admin/" : `/admin/user/details/${props.id}`;
}

function onChange(option: DeleteUserPageOption): void {
  isConfirming.value = false;
  // Ensure dependencies are consistent
  if (!option.selected) {
    // Select options it depends on
    options.value.forEach((o) => {
      if (option.DependsOn.includes(o.Type)) {
        o.selected = false;
      }
    });
  } else {
    // Deselect options it's depended upon by
    options.value.forEach((o) => {
      if (option.DependedOnBy.includes(o.Type)) {
        o.selected = true;
      }
    });
  }
  isValid.value = options.value.some((o) => o.selected);
}

function deleteUser(): void {
  const optionValues = options.value.reduce((obj, o) => {
    obj[o.Type] = o.selected ? "Delete" : "Leave";
    return obj;
  }, {});

  const data = {
    Values: optionValues,
  };

  axios.post(`/api/userdeletion/${props.id}`, data).then((resp) => {
    logMessages.value = resp.data as string[];
  });
}

onMounted(() => {
  axios.get(`/api/userdeletion/${props.id}`).then((resp) => {
    const model = resp.data as UserDeletionOptionsApiModel;
    debug(model);
    shortUserDetails.value = model.ShortUserDetails;
    options.value = model.Options.map((o: UserDeletionDataTypeAndDependencies) => ({
      // Augment the server model with additional properties
      ...o,
      selected: true,
      description: optionMetadata[o.Type].description,
      visible: true,
    }));
  });
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less"></style>
